import React, { useState } from "react";
import { Info, Check } from "lucide-react";
import { Popover, PopoverTrigger, PopoverContent } from "./ui/popover";

const RED = "#E4002B";
const FILL = "#EAF1FA";
const STROKE = "#1F2937";
const BLOB = "M30 6c6 0 8 6 13 7s11-2 13 4-4 10-3 15 7 9 3 14-10 1-15 3-7 9-13 8-6-7-11-9-12 0-13-6 6-9 6-14-7-9-3-14 10 0 14-3 4-5 9-5z";

export const selCls = (on) =>
  `rounded-2xl border-2 bg-white transition-[border-color,background-color,box-shadow,transform] duration-200 hover:-translate-y-0.5 hover:shadow-md ${on ? "border-[#E4002B] bg-[#FFF1F3] shadow-sm" : "border-[#ECE7E0]"}`;

export function ShapeIcon({ shape, size = 56 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 60 60" aria-hidden="true">
      {shape === "Square" && <rect x="10" y="10" width="40" height="40" rx="3" fill={FILL} stroke={STROKE} strokeWidth="1.8" />}
      {shape === "Round" && <circle cx="30" cy="30" r="21" fill={FILL} stroke={STROKE} strokeWidth="1.8" />}
      {shape === "Rectangle" && <rect x="4" y="16" width="52" height="28" rx="3" fill={FILL} stroke={STROKE} strokeWidth="1.8" />}
      {shape === "Oval" && <ellipse cx="30" cy="30" rx="26" ry="17" fill={FILL} stroke={STROKE} strokeWidth="1.8" />}
      {shape === "Custom" && <path d={BLOB} fill={FILL} stroke={STROKE} strokeWidth="1.8" strokeLinejoin="round" />}
    </svg>
  );
}

export function ShapeCard({ shape, active, onClick }) {
  return (
    <button onClick={onClick} aria-pressed={active} className={`${selCls(active)} p-4 flex flex-col items-center gap-2`} data-testid={`roll-shape-${shape.toLowerCase()}`}>
      <ShapeIcon shape={shape} />
      <span className="font-semibold text-sm">{shape}</span>
    </button>
  );
}

// Technical size diagram: true aspect ratio, longer side grows with size.
export function SizeDiagram({ w, h, shape }) {
  const L = 34 + (Math.max(w, h) / 150) * 46;
  const pw = w >= h ? L : L * (w / h);
  const ph = h >= w ? L : L * (h / w);
  const W = 120, H = 112, x = 22 + (W - 22 - pw) / 2, y = 18 + (H - 18 - ph) / 2;
  const round = shape === "Round" || shape === "Oval";
  const dim = "#2563EB";
  const t = { fontSize: 10, fontWeight: 600, fill: dim };
  return (
    <svg width={W} height={H} viewBox={`0 0 ${W} ${H}`} aria-hidden="true">
      {round ? <ellipse cx={x + pw / 2} cy={y + ph / 2} rx={pw / 2} ry={ph / 2} fill={FILL} stroke={STROKE} strokeWidth="1.4" />
        : <rect x={x} y={y} width={pw} height={ph} rx="3" fill={FILL} stroke={STROKE} strokeWidth="1.4" strokeDasharray={shape === "Custom" ? "4 3" : undefined} />}
      <line x1={x} y1={y - 6} x2={x + pw} y2={y - 6} stroke={dim} strokeWidth="1" />
      <line x1={x} y1={y - 9} x2={x} y2={y - 3} stroke={dim} strokeWidth="1" />
      <line x1={x + pw} y1={y - 9} x2={x + pw} y2={y - 3} stroke={dim} strokeWidth="1" />
      <text x={x + pw / 2} y={y - 9} textAnchor="middle" {...t}>{`${w} mm`}</text>
      <line x1={x - 6} y1={y} x2={x - 6} y2={y + ph} stroke={dim} strokeWidth="1" />
      <line x1={x - 9} y1={y} x2={x - 3} y2={y} stroke={dim} strokeWidth="1" />
      <line x1={x - 9} y1={y + ph} x2={x - 3} y2={y + ph} stroke={dim} strokeWidth="1" />
      <text x={x - 9} y={y + ph / 2} textAnchor="middle" {...t} transform={`rotate(-90 ${x - 9} ${y + ph / 2})`}>{`${h} mm`}</text>
    </svg>
  );
}

export function SizeTile({ size, shape, active, onClick }) {
  return (
    <button onClick={onClick} aria-pressed={active} className={`${selCls(active)} px-2 pt-2 pb-3 flex flex-col items-center`} data-testid={`roll-size-${size.id}`}>
      <SizeDiagram w={size.w} h={size.h} shape={shape} />
      <span className="font-semibold text-sm whitespace-nowrap">{size.label}</span>
    </button>
  );
}

export function InfoTip({ text, testid }) {
  const [open, setOpen] = useState(false);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <span role="button" tabIndex={0} aria-label="More information" onClick={(e) => { e.stopPropagation(); setOpen((o) => !o); }}
          onMouseEnter={() => setOpen(true)} onMouseLeave={() => setOpen(false)}
          className="inline-flex text-slate-500 hover:text-[#E4002B] cursor-pointer" data-testid={testid}>
          <Info size={17} />
        </span>
      </PopoverTrigger>
      <PopoverContent side="top" collisionPadding={12} className="w-64 text-sm leading-snug" data-testid={`${testid}-content`}>{text}</PopoverContent>
    </Popover>
  );
}

export function StockCard({ stock, active, onClick }) {
  const id = stock.name.toLowerCase().replace(/\s+/g, "-");
  return (
    <div role="button" tabIndex={0} onClick={onClick} onKeyDown={(e) => e.key === "Enter" && onClick()} aria-pressed={active}
      className={`${selCls(active)} overflow-hidden text-left cursor-pointer relative`} data-testid={`roll-stock-${id}`}>
      {active && <span className="absolute top-3 right-3 h-7 w-7 rounded-full bg-[#E4002B] text-white flex items-center justify-center z-10"><Check size={16} strokeWidth={3} /></span>}
      <div className="aspect-[3/2] bg-[#F7F4F0] overflow-hidden">
        <img src={stock.image} alt={`${stock.name} label stock`} className="w-full h-full object-cover" />
      </div>
      <div className="p-5">
        <div className="flex items-center gap-2">
          <span className="font-display text-lg font-extrabold">{stock.name}</span>
          <InfoTip text={stock.desc} testid={`roll-stock-info-${id}`} />
        </div>
        <p className="text-sm text-slate-600 mt-1.5 leading-relaxed">{stock.desc}</p>
      </div>
    </div>
  );
}

// Corner close-up at a fixed scale (8 px per mm) so the radii compare directly; dashed red = square corner.
export function CornerDiagram({ r }) {
  const R = r * 8;
  return (
    <svg width="100" height="72" viewBox="0 0 100 72" aria-hidden="true">
      <path d={`M6 72 V${6 + R} A${R} ${R} 0 0 1 ${6 + R} 6 H100 V72 Z`} fill={FILL} />
      <path d={`M6 72 V${6 + R} A${R} ${R} 0 0 1 ${6 + R} 6 H100`} fill="none" stroke={STROKE} strokeWidth="2" />
      <path d="M6 52 V6 H52" fill="none" stroke={RED} strokeWidth="1.2" strokeDasharray="3 3" opacity="0.7" />
    </svg>
  );
}

export function CornerTile({ r, active, onClick }) {
  return (
    <button onClick={onClick} aria-pressed={active} className={`${selCls(active)} p-4 flex flex-col items-center gap-2`} data-testid={`roll-corner-${r}`}>
      <CornerDiagram r={r} />
      <span className="font-semibold text-sm">{r} mm</span>
    </button>
  );
}

export function RollStep({ n, title, hint, children, testid }) {
  return (
    <div data-testid={testid}>
      <div className="flex items-start gap-4 mb-5">
        <span className="h-10 w-10 rounded-full bg-[#F3EFEA] font-display font-black flex items-center justify-center flex-shrink-0">{n}</span>
        <div>
          <h2 className="font-display text-2xl font-extrabold tracking-tight">{title}</h2>
          {hint && <p className="text-sm text-slate-500 mt-0.5">{hint}</p>}
        </div>
      </div>
      {children}
    </div>
  );
}
